import { useDeleteApiToken } from '@/queries/api-tokens';
import { getErrorMessage } from '@/utils';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import KeyOutlined from '@ant-design/icons/KeyOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import App from 'antd/es/app';
import Modal from 'antd/es/modal';
import React, { useState } from 'react';
import styled from 'styled-components';
import { DataTable, createActionColumn } from 'ui-components';
import type { ActionItem, DataTableColumnType } from 'ui-components';

import type { ApiTokenResponse } from '@mai/platform-api/src/routes/api-tokens.schema';

const WarningText = styled.p`
  ${({ theme }) => `
  color: ${theme.colorError};
  margin-top: ${theme.marginXS}px;
`}
`;

interface ApiTokensTableProps {
  tokens: ApiTokenResponse[];
  loading: boolean;
  onEdit: (token: ApiTokenResponse) => void;
  onView: (token: ApiTokenResponse) => void;
  onCreateToken: () => void;
  columns: DataTableColumnType<ApiTokenResponse>[];
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    showTotal?: (total: number, range: [number, number]) => string;
    onChange?: (page: number, pageSize: number) => void;
  };
}

export const ApiTokensTable: React.FC<ApiTokensTableProps> = ({
  tokens,
  loading,
  onEdit,
  onView,
  onCreateToken,
  columns,
  pagination,
}) => {
  const { message } = App.useApp();
  const deleteTokenMutation = useDeleteApiToken();
  const [deletingTokenId, setDeletingTokenId] = useState<string | null>(null);

  const handleDelete = (token: ApiTokenResponse) => {
    Modal.confirm({
      title: 'Delete API Token',
      content: (
        <div>
          <p>Are you sure you want to delete the API token "{token.name}"?</p>
          <WarningText>
            This action cannot be undone and will immediately invalidate all
            sessions using this token.
          </WarningText>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setDeletingTokenId(token.tokenId);
          await deleteTokenMutation.mutateAsync(token.tokenId);
          void message.success('API token deleted successfully');
        } catch (error) {
          void message.error(
            getErrorMessage(error, 'Failed to delete API token'),
          );
        } finally {
          setDeletingTokenId(null);
        }
      },
    });
  };

  const handleCopyId = async (tokenId: string) => {
    try {
      await navigator.clipboard.writeText(tokenId);
      void message.success('Token ID copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy token ID');
    }
  };

  const getTokenActions = (
    token: ApiTokenResponse,
  ): (ActionItem | { type: 'divider' })[] => [
    {
      key: 'view',
      label: 'View Details',
      icon: <EyeOutlined />,
      onClick: () => onView(token),
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => onEdit(token),
    },
    {
      key: 'copy-id',
      label: 'Copy Token ID',
      icon: <CopyOutlined />,
      onClick: () => handleCopyId(token.tokenId),
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDelete(token),
      disabled: deletingTokenId === token.tokenId,
    },
  ];

  const finalColumns: DataTableColumnType<ApiTokenResponse>[] = [
    ...columns,
    createActionColumn<ApiTokenResponse>({
      getActions: getTokenActions,
      width: 80,
      align: 'right',
    }),
  ];

  return (
    <DataTable
      columns={finalColumns}
      data={tokens}
      loading={loading}
      rowKey="tokenId"
      maxVisibleFilters={3}
      pagination={pagination}
      emptyState={{
        icon: <KeyOutlined />,
        title: 'No API tokens yet',
        description:
          'Create your first API token to start accessing the API programmatically.',
        action: {
          label: 'Create Your First Token',
          onClick: onCreateToken,
          icon: <PlusOutlined />,
        },
        overrideOnFiltersPresent: {
          title: 'No tokens match your filters',
          description: 'Try adjusting your filters to see more results.',
          action: undefined,
        },
      }}
    />
  );
};
